export interface AmbrConfig {
  ambrApiUrl: string
  cognito: CognitoAuthOptions
  passwordResetUrl: string
  signUpUrl: string
  mlServiceUrl: string
  /**
   * Whether the Cognito message template delivers a verification link rather
   * than a numeric code. Account confirmation points the user at the emailed
   * link when set, and prompts for a code otherwise. Defaults to code entry.
   */
  emailVerificationLinks?: boolean
  /** Origin of the Motif Review app, absent where the cross-product switcher is off. */
  motifReviewUrl?: string
}

export interface CookieStorageData {
  domain?: string
  path?: string
  expires?: number
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
}

export interface CognitoAuthOptions {
  userPoolId?: string
  userPoolWebClientId?: string
  identityPoolId?: string
  region?: string
  mandatorySignIn?: boolean
  cookieStorage?: CookieStorageData
  refreshHandlers?: object
  storage?: CookieStorageData
  authenticationFlowType?: string
  identityPoolRegion?: string
  clientMetadata?: Record<string, string>
  endpoint?: string
}

import { createLogger } from '../logger'

const configLogger = createLogger('config')

export class ConfigProvider {
  private static ambrConfigName: string = 'ambrConfig'
  private static configsByName = new Map<string, unknown>()

  public static register(config: AmbrConfig) {
    ConfigProvider.registerConfig(ConfigProvider.ambrConfigName, config)
  }

  public static get Config(): AmbrConfig {
    return ConfigProvider.getConfig<AmbrConfig>(ConfigProvider.ambrConfigName)
  }

  /** The config, or `null` when none is registered. {@link Config} throws in that case. */
  public static get ConfigOrNull(): AmbrConfig | null {
    return (ConfigProvider.configsByName.get(ConfigProvider.ambrConfigName) as AmbrConfig) ?? null
  }

  public static registerConfig<T>(name: string, config: T) {
    if (this.configsByName.has(name)) {
      configLogger.info(`Config ${name} already initialized, ignoring.`, { configName: name })
    } else {
      this.configsByName.set(name, config)
    }
  }

  public static getConfig<T>(name: string): T {
    if (!this.configsByName.has(name)) {
      throw new Error(`Config ${name} not initialized.`)
    }

    return this.configsByName.get(name) as T
  }
}
